import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import debounce from "lodash/debounce";

import webLinkImg from '../../images/link.svg';
import instagramImg from '../../images/instagram.svg';
import facebookImg from '../../images/facebook.svg';
import linkedinImg from '../../images/linkedin.svg';
import youtubeImg from '../../images/youtube.svg';
import twitterImg from '../../images/twitter.svg';

import { uploadMediaFile } from "../../utills/fileUpload";
import { fetchAllBookCategoriesByLangOptions, fetchAllGenresByLangOptions, fetchAllProfessionOptions } from "../../helper/index";
import { useLanguage } from "../../context/languageContext";
import { mergeAutoBooksWritten } from "./booksWritten";


const useForm = (callback, validate) => {
  const { selectedLang, setSelectedLang, languages, adminInfo } = useLanguage();

  const createInitialLangValues = (lang) => ({
    langCode: lang,
    name: "",
    profession: [],
    recommendedBooks: [],
    recommendedGenres: [],
    booksWritten: [],
    about: "",
    type: "NonFiction"
  });

  const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

  const [values, setValues] = useState({
    image: null,
    mediaLink: [],
    translations: createInitialTranslationsArr(),
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [editMediaError, setEditMediaError] = useState("");
  const [recommendeGenres, setRecommendeGenres] = useState([]);
  const [recommendeBooks, setRecommendeBooks] = useState([]);
  const [booksWritten, setBooksWritten] = useState([]);
  // The bookIds the last author-name lookup put into Books Written — the only
  // entries the next lookup may take back out.
  const autoWrittenIdsRef = useRef(new Set());
  // Bumped by `resetAutoBooksWritten`: a lookup started before the bump
  // answers for a form that is no longer showing that person, and is dropped.
  const autoLookupGenRef = useRef(0);

  // Books Written is one list for every language, like Recommended Books: the
  // server names each entry per language from the English list on save.
  const setBooksWrittenEverywhere = useCallback((update) => {
    setValues((prev) => ({
      ...prev,
      translations: prev.translations.map((tr) => ({ ...tr, booksWritten: update(tr.booksWritten || []) })),
    }));
  }, []);

  const applyAutoBooksWritten = useCallback((matches) => {
    const previousAuto = autoWrittenIdsRef.current;
    autoWrittenIdsRef.current = new Set(matches.map((book) => String(book.bookId)));
    setBooksWrittenEverywhere((current) => mergeAutoBooksWritten(current, matches, previousAuto));
  }, [setBooksWrittenEverywhere]);

  useEffect(() => {
    if (adminInfo && adminInfo.token) {
      fetchAllBooksAndGenressData();
    }
  }, [adminInfo, selectedLang]);

  const handleSelectLanguage = (language) => {
    setSelectedLang(language);

    setValues((prevValues) => {
      const isLanguagesExists = prevValues.translations.some((lang) => lang.langCode === language);

      if (isLanguagesExists) {
        return { ...prevValues };
      }

      return {
        ...prevValues,
        translations: [...prevValues.translations, createInitialLangValues(language)],
      };
    });
  };

  const handleUpdateGenresByBooks = async (value, language) => {
    try {
      const headers = { Authorization: adminInfo.token ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/genres-by-books`, { value, language }, { headers });

      if (response.status) {
        const allGenresByBooks = response?.data || [];
        const genresByBooks = allGenresByBooks.map((genre) => ({
          genreName: genre.genreName,
          genreId: genre.genreId,
          type: genre.type,
        }));

        setValues((prevValues) => {
          const updatedTranslations = prevValues.translations.map((translation) => {
            if (translation.langCode === language) {
              return {
                ...translation,
                recommendedGenres: genresByBooks,
              }
            }
            return translation;
          });

          return {
            ...prevValues,
            translations: updatedTranslations,
          };
        });
      }
    } catch (error) {
      console.error("handleUpdateGenresBySelectedBooks catch error...", error)
    }
  };

  const debouncedFetchBooks = useMemo(() => {
    return debounce(async (value, language) => {
      const generation = autoLookupGenRef.current;
      try {
        // Sent as typed: the API ignores case. Capitalising each word here
        // broke names like "McKeown" and "bell hooks".
        const authorName = value.trim();

        const headers = { Authorization: adminInfo?.token ? `Bearer ${adminInfo.token}` : null };
        const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/books-by-recommenders`,
          { value: authorName, language },
          { headers }
        );

        if (generation !== autoLookupGenRef.current) return;
        if (response.status) {
          const booksWrittenData = response?.data || [];
          const booksWritten = booksWrittenData.map((book) => ({
            bookName: book.bookName,
            bookId: book.bookId,
          }));

          const booksWrittenSelectOptions = booksWrittenData.map((book) => ({
            label: book.bookName,
            value: book.bookId,
            authorName: book.authorName,
            image: book.image,
          }));
          setBooksWritten(booksWrittenSelectOptions);
          applyAutoBooksWritten(booksWritten);
        }
      } catch (error) {
        console.error("books-by-recommenders error:", error);
      }
    }, 1000);
  }, [adminInfo, applyAutoBooksWritten]);

  useEffect(() => {
    return () => debouncedFetchBooks.cancel();
  }, [debouncedFetchBooks]);

  /**
   * Forgets the name auto-fill's state — called when the edit popup loads a
   * recommender. The popup stays mounted, so a lookup still pending from the
   * previous person would land that person's books on this one, and the
   * remembered ids would let a later lookup take this person's books out.
   */
  const resetAutoBooksWritten = useCallback(() => {
    debouncedFetchBooks.cancel();
    autoLookupGenRef.current += 1;
    autoWrittenIdsRef.current = new Set();
  }, [debouncedFetchBooks]);

  const handleGetBooksWrittenByRecommender = useCallback((value, language) => {
    const trimmed = (value || "").trim();

    if (!trimmed) {
      debouncedFetchBooks.cancel();
      setBooksWritten([]);
      applyAutoBooksWritten([]);
      return;
    }

    debouncedFetchBooks(trimmed, language);
  },
    [debouncedFetchBooks, applyAutoBooksWritten]
  );

  const handleUpdateTranslationField = async (fieldName, value, langCode) => {
    setValues((prevValues) => {
      const updatedTranslations = prevValues.translations.map((translation) =>
        translation.langCode === langCode
          ? {
            ...translation,
            [fieldName]: value,
            ...(fieldName === "type" && { recommendedGenres: [] }),
          }
          : translation
      );

      return {
        ...prevValues,
        translations: updatedTranslations,
      };
    });

    if (fieldName === "recommendedBooks") {
      await handleUpdateGenresByBooks(value, langCode);
    }

    if (fieldName === "name") {
      await handleGetBooksWrittenByRecommender(value, langCode);
    }
  };

  // One recommended list for every language: the admin orders books and
  // series once, and the server names each entry per language from the
  // English list on save. Written to all of them here so a reorder made while
  // another language is open is not the one thrown away.
  const handleUpdateRecommendedBooks = async (nextBooks, langCode) => {
    setValues((prevValues) => ({
      ...prevValues,
      translations: prevValues.translations.map((translation) => ({ ...translation, recommendedBooks: nextBooks })),
    }));

    await handleUpdateGenresByBooks(nextBooks, langCode);
  };

  const handleGetTranslationByLang = (langCode) => {
    return values.translations?.find((lang) => lang.langCode === langCode) || {};
  };

  const fetchAllBooksAndGenressData = async () => {
    const genresOptions = await fetchAllGenresByLangOptions(selectedLang, adminInfo.token);
    const bookNameOptions = await fetchAllBookCategoriesByLangOptions(selectedLang, adminInfo.token);

    const options = genresOptions.map((genre) => ({
      label: genre.genreName,
      value: genre.genreId,
      type: genre.type,
    }));

    const bookOptions = bookNameOptions.map((book) => ({
      label: book.bookName,
      value: book.bookId,
      authorName: book.authorName,
      image: book.image,
    }));

    setRecommendeGenres(options);
    setRecommendeBooks(bookOptions);
  };

  // The catalogue is fetched now rather than bundled, so it is state and
  // refetches when the editing language changes — the labels are per
  // language, and the ids are the catalogue's own.
  const [fieldOptions, setFieldOptions] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;

    let alive = true;

    fetchAllProfessionOptions(adminInfo.token, selectedLang).then((options) => {
      if (alive) setFieldOptions(options);
    });

    return () => {
      alive = false;
    };
  }, [adminInfo?.token, selectedLang]);

  const handleAddMediaLinkValues = (value, key) => {
    setValues((prevValues) => {
      const updatedMediaLinks = prevValues.mediaLink.filter(item => item.key !== key);

      return {
        ...prevValues,
        mediaLink: [...updatedMediaLinks, { key: key, link: value }],
      };
    });
  };

  const handleUploadMedia = async (event) => {
    const result = await uploadMediaFile(event, true);

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      setValues((prevValues) => ({
        ...prevValues,
        image: result[0]?.data,
      }));
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting) {
      callback();
    }
  }, [errors, isSubmitting]);

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    setErrors(validate(values, selectedLang));
    setIsSubmitting(true);
  };

  const socialMediaLinks = [
    { key: "website", imgSrc: webLinkImg, alt: "Web Link" },
    { key: "instagram", imgSrc: instagramImg, alt: "Instagram" },
    { key: "facebook", imgSrc: facebookImg, alt: "Facebook" },
    { key: "linkedin", imgSrc: linkedinImg, alt: "LinkedIn" },
    { key: "youtube", imgSrc: youtubeImg, alt: "Youtube" },
    { key: "twitter", imgSrc: twitterImg, alt: "Twitter" },
  ];

  const optionStyles = useMemo(() => ({
    container: { display: 'flex', alignItems: 'center', gap: '10px' },
    image: { width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px' },
    textContainer: { flex: 1 },
    bookName: { margin: 0, marginBottom: '2px' },
    authorName: { margin: 0, fontSize: '0.85em', color: "var(--ps-text-muted)" }
  }), []);


  // The Books Written shelf hands back the whole list, in order. An auto-filled
  // book the admin removed is no longer the auto-fill's to manage.
  const handleUpdateBooksWritten = (nextItems) => {
    const ids = new Set(nextItems.map((entry) => String(entry.bookId)));
    autoWrittenIdsRef.current = new Set([...autoWrittenIdsRef.current].filter((id) => ids.has(id)));
    setBooksWrittenEverywhere(() => nextItems);
  };

  return {
    resetAutoBooksWritten,
    values,
    createInitialTranslationsArr,
    errors,
    setValues,
    setIsSubmit,
    isSubmit,
    editMediaError,
    languages,
    socialMediaLinks,
    recommendeGenres,
    recommendeBooks,
    booksWritten,
    fieldOptions,

    handleGetTranslationByLang,
    handleSelectLanguage,
    handleUpdateTranslationField,
    handleUpdateRecommendedBooks,
    handleUpdateBooksWritten,
    handleAddMediaLinkValues,
    handleUploadMedia,
    handleSubmit,
    handleGetBooksWrittenByRecommender,
  }
};

export default useForm;
